import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Clock, TrendUp, TrendDown, Newspaper, Trophy, Lightning, Lock, Star, Gift, Target, Coins, WifiSlash } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, ReferenceLine } from 'recharts';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { toast } from 'sonner';
import { saveNewsReactorProgress, getNewsReactorProgress, addToPendingSync, isOnline } from '../lib/offlineStorage';
import { showXpToasts } from '../lib/progression';
import { useAuth } from '../context/AuthContext';

// Level configuration - 100 levels in 5 tiers
const TIER_CONFIG = {
  beginner: { levels: [1, 20], newsInterval: 8000, targetReturn: 5, name: 'Beginner', color: 'emerald' },
  intermediate: { levels: [21, 40], newsInterval: 6000, targetReturn: 8, name: 'Intermediate', color: 'blue' },
  advanced: { levels: [41, 60], newsInterval: 5000, targetReturn: 12, name: 'Advanced', color: 'purple' },
  expert: { levels: [61, 80], newsInterval: 4000, targetReturn: 15, name: 'Expert', color: 'orange' },
  master: { levels: [81, 100], newsInterval: 3000, targetReturn: 20, name: 'Master', color: 'red' },
};

// Rewards for every 10 levels
const LEVEL_REWARDS = {
  10: 500, 20: 1000, 30: 1500, 40: 2000, 50: 3000,
  60: 4000, 70: 5000, 80: 7000, 90: 8000, 100: 15000
};

// Milestone badges
const MILESTONES = {
  25: { bonus: 250, badge: '🎯 Quarter Master' },
  50: { bonus: 500, badge: '⭐ Halfway Hero' },
  75: { bonus: 1000, badge: '🔥 Elite Trader' },
  100: { bonus: 15000, badge: '💎 News Master' }
};

// Sample stocks for the game
const GAME_STOCKS = [
  { symbol: 'TECHX', name: 'TechX Corporation', sector: 'Technology' },
  { symbol: 'PHARMO', name: 'PharmaCo Labs', sector: 'Healthcare' },
  { symbol: 'GREENV', name: 'GreenVolt Energy', sector: 'Energy' },
  { symbol: 'FINTECH', name: 'FinTech Global', sector: 'Finance' },
  { symbol: 'RETAIL', name: 'RetailMax Inc', sector: 'Retail' },
  { symbol: 'AEROSP', name: 'AeroSpace Dynamics', sector: 'Aerospace' },
  { symbol: 'CRYPTO', name: 'CryptoVault Inc', sector: 'Crypto' },
  { symbol: 'GAMING', name: 'GameZone Studios', sector: 'Gaming' },
];

// News templates by sentiment
const NEWS_TEMPLATES = {
  positive: [
    { text: '📈 {company} announces record quarterly earnings!', impact: [0.03, 0.06] },
    { text: '✅ FDA approves {company}\'s breakthrough product!', impact: [0.05, 0.10] },
    { text: '🤝 {company} secures major partnership deal!', impact: [0.02, 0.05] },
    { text: '🚀 {company} launches revolutionary new product!', impact: [0.04, 0.08] },
    { text: '💰 {company} raises full-year guidance significantly!', impact: [0.03, 0.06] },
    { text: '📊 Analysts upgrade {company} to "Strong Buy"!', impact: [0.02, 0.04] },
    { text: '🌍 {company} expands into 10 new markets!', impact: [0.03, 0.05] },
    { text: '💎 {company} announces $1B stock buyback!', impact: [0.04, 0.07] },
  ],
  negative: [
    { text: '📉 {company} misses earnings expectations badly!', impact: [-0.03, -0.06] },
    { text: '❌ Regulators reject {company}\'s key product!', impact: [-0.05, -0.10] },
    { text: '⚠️ {company} faces unexpected supply chain crisis!', impact: [-0.03, -0.05] },
    { text: '🔴 {company} CEO resigns amid scandal!', impact: [-0.04, -0.08] },
    { text: '⬇️ {company} slashes revenue forecast by 20%!', impact: [-0.04, -0.07] },
    { text: '📊 Analysts downgrade {company} to "Sell"!', impact: [-0.02, -0.04] },
    { text: '⚖️ {company} faces major class-action lawsuit!', impact: [-0.03, -0.06] },
    { text: '🏭 {company} announces massive layoffs!', impact: [-0.03, -0.05] },
  ],
  neutral: [
    { text: '📰 {company} to present at industry conference', impact: [-0.01, 0.01] },
    { text: '🔄 {company} announces routine executive changes', impact: [-0.01, 0.01] },
  ]
};

const getTierForLevel = (level) => {
  for (const [tier, config] of Object.entries(TIER_CONFIG)) {
    if (level >= config.levels[0] && level <= config.levels[1]) {
      return { tier, ...config };
    }
  }
  return { tier: 'beginner', ...TIER_CONFIG.beginner };
};

const NewsReactor = () => {
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const [gameState, setGameState] = useState('menu'); // menu, levelSelect, playing, results
  const [userProgress, setUserProgress] = useState({ currentLevel: 1, completedLevels: [], totalEarned: 0, badges: [] });
  const [selectedLevel, setSelectedLevel] = useState(1);
  const [stock, setStock] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(100);
  const [startPrice, setStartPrice] = useState(100);
  const [cash, setCash] = useState(10000);
  const [shares, setShares] = useState(0);
  const [entryPrice, setEntryPrice] = useState(null);
  const [news, setNews] = useState([]);
  const [timeLeft, setTimeLeft] = useState(60);
  const [currentReturn, setCurrentReturn] = useState(0);
  const [levelPassed, setLevelPassed] = useState(false);
  const [reward, setReward] = useState(null);
  const gameLoopRef = useRef(null);
  const newsIntervalRef = useRef(null);

  // Fetch user's News Reactor progress on mount
  const fetchProgress = useCallback(async () => {
    // Try to get from server first
    if (isOnline()) {
      try {
        const res = await api.get('/news-reactor/progress');
        setUserProgress(res.data);
        // Cache for offline use
        saveNewsReactorProgress(res.data);
        return;
      } catch (error) {
        console.warn('[NewsReactor] Failed to fetch from server, trying offline cache:', error?.message || error);
      }
    }

    // Fallback to offline cache
    const cachedProgress = getNewsReactorProgress();
    if (cachedProgress) {
      setUserProgress(cachedProgress);
      toast.info('Playing offline - progress will sync when reconnected');
    } else {
      console.log('No existing progress, starting fresh');
    }
  }, []);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const startLevel = useCallback((level) => {
    const tierConfig = getTierForLevel(level);
    const randomStock = GAME_STOCKS[Math.floor(Math.random() * GAME_STOCKS.length)];
    const basePrice = 50 + Math.random() * 150; // Random price between 50-200

    setSelectedLevel(level);
    setStock(randomStock);
    setCurrentPrice(basePrice);
    setStartPrice(basePrice);
    setPriceHistory([{ time: 0, price: basePrice }]);
    setCash(10000);
    setShares(0);
    setEntryPrice(null);
    setNews([]);
    setTimeLeft(60);
    setCurrentReturn(0);
    setLevelPassed(false);
    setReward(null);
    setGameState('playing');
  }, []);

  // Game loop - price updates
  useEffect(() => {
    if (gameState !== 'playing') return;

    let time = 0;
    const tierConfig = getTierForLevel(selectedLevel);

    gameLoopRef.current = setInterval(() => {
      time += 1;

      setCurrentPrice(prev => {
        // Small random fluctuation
        const volatility = 0.005 + (selectedLevel / 100) * 0.01;
        const change = (Math.random() - 0.5) * volatility * prev;
        const newPrice = Math.max(1, prev + change);

        setPriceHistory(history => {
          const newHistory = [...history, { time, price: newPrice }];
          return newHistory.slice(-60);
        });

        // Calculate current return
        const portfolioValue = cash + (shares * newPrice);
        const returnPct = ((portfolioValue - 10000) / 10000) * 100;
        setCurrentReturn(returnPct);

        // Check if target reached
        if (returnPct >= tierConfig.targetReturn && !levelPassed) {
          setLevelPassed(true);
        }

        return newPrice;
      });

      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(gameLoopRef.current);
          clearInterval(newsIntervalRef.current);
          setGameState('results');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(gameLoopRef.current);
  }, [gameState, selectedLevel, cash, shares, levelPassed]);

  // News generation
  useEffect(() => {
    if (gameState !== 'playing' || !stock) return;

    const tierConfig = getTierForLevel(selectedLevel);

    const generateNews = () => {
      const rand = Math.random();
      let category = rand < 0.45 ? 'positive' : rand < 0.9 ? 'negative' : 'neutral';

      const templates = NEWS_TEMPLATES[category];
      const template = templates[Math.floor(Math.random() * templates.length)];
      const [minImpact, maxImpact] = template.impact;
      const actualImpact = minImpact + Math.random() * (maxImpact - minImpact);

      const newsItem = {
        id: Date.now(),
        text: template.text.replace('{company}', stock.name),
        category,
        time: new Date().toLocaleTimeString()
      };

      setNews(prev => [newsItem, ...prev].slice(0, 5));

      // Apply impact to price
      setCurrentPrice(prev => {
        const impactedPrice = prev * (1 + actualImpact);
        return Math.max(1, impactedPrice);
      });
    };

    // First news after 2 seconds
    setTimeout(generateNews, 2000);
    newsIntervalRef.current = setInterval(generateNews, tierConfig.newsInterval);

    return () => clearInterval(newsIntervalRef.current);
  }, [gameState, stock, selectedLevel]);

  const handleBuy = () => {
    if (cash <= 0) return;
    const sharesToBuy = Math.floor(cash / currentPrice);
    if (sharesToBuy <= 0) return;

    const cost = sharesToBuy * currentPrice;
    setCash(prev => prev - cost);
    setShares(prev => prev + sharesToBuy);
    if (!entryPrice) setEntryPrice(currentPrice);
  };

  const handleSell = () => {
    if (shares <= 0) return;
    const proceeds = shares * currentPrice;
    setCash(prev => prev + proceeds);
    setShares(0);
    setEntryPrice(null);
  };

  const handleLevelComplete = async () => {
    if (!levelPassed) {
      setGameState('menu');
      return;
    }

    // Calculate local rewards
    const calculateLocalReward = () => {
      let cash = 0;
      let badge = null;
      
      if (LEVEL_REWARDS[selectedLevel]) {
        cash += LEVEL_REWARDS[selectedLevel];
      }
      if (MILESTONES[selectedLevel]) {
        cash += MILESTONES[selectedLevel].bonus;
        badge = MILESTONES[selectedLevel].badge;
      }
      
      return { cash, badge };
    };

    // Try online first
    if (isOnline()) {
      try {
        const res = await api.post('/news-reactor/complete-level', {
          level: selectedLevel,
          returnPct: currentReturn
        });

        setUserProgress(res.data.progress);
        setReward(res.data.reward);
        
        // Cache progress
        saveNewsReactorProgress(res.data.progress);

        if (res.data.reward?.cash > 0) {
          toast.success(`+$${res.data.reward.cash.toLocaleString()} added to your portfolio!`);
        }
        showXpToasts(res.data.xp_result, { refreshUser: fetchUser });
        return;
      } catch (error) {
        console.error('Failed to save progress online:', error);
      }
    }
    
    // Offline mode - save locally
    const localReward = calculateLocalReward();
    const newProgress = {
      ...userProgress,
      currentLevel: Math.max(userProgress.currentLevel, selectedLevel + 1),
      completedLevels: [...(userProgress.completedLevels || []), selectedLevel],
      totalEarned: (userProgress.totalEarned || 0) + localReward.cash,
      badges: localReward.badge 
        ? [...(userProgress.badges || []), localReward.badge]
        : userProgress.badges || []
    };
    
    setUserProgress(newProgress);
    setReward(localReward);
    
    // Save to local storage
    saveNewsReactorProgress(newProgress);
    
    // Queue for sync when back online
    addToPendingSync({
      type: 'news_reactor_level',
      level: selectedLevel,
      returnPct: currentReturn
    });
    
    if (localReward.cash > 0) {
      toast.success(`+$${localReward.cash.toLocaleString()} earned! Will sync when online.`, {
        icon: '📴'
      });
    }
  };

  // Menu Screen
  if (gameState === 'menu') {
    const nextMilestone = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100].find(m => m > userProgress.currentLevel - 1) || 100;
    const nextReward = LEVEL_REWARDS[nextMilestone] || 0;

    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-12">
          <button
            onClick={() => navigate('/game-modes')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Game Modes
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="inline-flex p-4 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-600 mb-6">
              <Lightning size={48} weight="fill" className="text-white" />
            </div>
            <h1 className="text-4xl font-unbounded font-bold text-white mb-2">News Reactor</h1>
            <p className="text-zinc-400 max-w-xl mx-auto">
              React to breaking news, hit your profit target, and earn real cash for your portfolio!
            </p>
            {!isOnline() && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-full text-orange-400 text-sm">
                <WifiSlash size={16} />
                Offline Mode - Progress will sync when reconnected
              </div>
            )}
          </motion.div>

          {/* Progress Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-500/30 rounded-2xl p-6 mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-zinc-400 text-sm">Your Progress</p>
                <p className="text-3xl font-bold text-white">Level {userProgress.currentLevel}/100</p>
              </div>
              <div className="text-right">
                <p className="text-zinc-400 text-sm">Total Earned</p>
                <p className="text-2xl font-bold text-gold">+${userProgress.totalEarned.toLocaleString()}</p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full h-3 bg-zinc-800 rounded-full overflow-hidden mb-4">
              <div 
                className="h-full bg-gradient-to-r from-yellow-500 to-orange-500 transition-all"
                style={{ width: `${(userProgress.currentLevel / 100) * 100}%` }}
              />
            </div>

            <div className="flex items-center justify-between text-sm">
              <p className="text-zinc-400">
                🎯 Next Reward: Level {nextMilestone}
              </p>
              <p className="text-gold font-bold">+${nextReward.toLocaleString()}</p>
            </div>

            {/* Badges */}
            {userProgress.badges && userProgress.badges.length > 0 && (
              <div className="mt-4 pt-4 border-t border-zinc-700">
                <p className="text-zinc-400 text-sm mb-2">Badges Earned:</p>
                <div className="flex flex-wrap gap-2">
                  {userProgress.badges.map((badge, i) => (
                    <span key={i} className="px-3 py-1 bg-zinc-800 rounded-full text-sm">{badge}</span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          {/* Tier Overview */}
          <div className="grid gap-4 mb-8">
            {Object.entries(TIER_CONFIG).map(([tier, config], index) => {
              const isUnlocked = userProgress.currentLevel >= config.levels[0];
              const isComplete = userProgress.currentLevel > config.levels[1];
              
              return (
                <motion.div
                  key={tier}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`p-4 rounded-xl border ${
                    isUnlocked 
                      ? `bg-${config.color}-500/10 border-${config.color}-500/30` 
                      : 'bg-zinc-900/50 border-zinc-800 opacity-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isUnlocked ? (
                        <Star size={24} weight="fill" className={`text-${config.color}-400`} />
                      ) : (
                        <Lock size={24} className="text-zinc-600" />
                      )}
                      <div>
                        <h3 className="text-white font-bold">{config.name}</h3>
                        <p className="text-zinc-500 text-sm">
                          Levels {config.levels[0]}-{config.levels[1]} • +{config.targetReturn}% target • News every {config.newsInterval/1000}s
                        </p>
                      </div>
                    </div>
                    {isComplete && <span className="text-emerald-400">✓ Complete</span>}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Play Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setGameState('levelSelect')}
            className="w-full py-5 bg-gradient-to-r from-yellow-500 to-orange-600 text-black font-bold text-xl rounded-xl"
          >
            <Play size={24} className="inline mr-2" />
            Play Level {userProgress.currentLevel}
          </motion.button>

          {/* Rewards Table */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 p-6 bg-zinc-900/50 border border-zinc-800 rounded-xl"
          >
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <Gift size={20} className="text-gold" />
              Reward Milestones
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {Object.entries(LEVEL_REWARDS).map(([level, reward]) => {
                const isEarned = userProgress.currentLevel > parseInt(level);
                return (
                  <div 
                    key={level} 
                    className={`p-3 rounded-lg text-center ${
                      isEarned ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-zinc-800/50'
                    }`}
                  >
                    <p className="text-zinc-400 text-xs">Level {level}</p>
                    <p className={`font-bold ${isEarned ? 'text-emerald-400' : 'text-gold'}`}>
                      +${reward.toLocaleString()}
                    </p>
                    {isEarned && <span className="text-emerald-400 text-xs">✓</span>}
                  </div>
                );
              })}
            </div>
            <p className="text-zinc-500 text-sm mt-4 text-center">
              Complete all 100 levels to earn <span className="text-gold font-bold">+$47,000</span> total!
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  // Level Select Screen
  if (gameState === 'levelSelect') {
    const currentTier = getTierForLevel(userProgress.currentLevel);

    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-12">
          <button
            onClick={() => setGameState('menu')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            Back
          </button>

          <h1 className="text-3xl font-unbounded font-bold text-white mb-8">Select Level</h1>

          <div className="grid grid-cols-5 md:grid-cols-10 gap-2 mb-8">
            {Array.from({ length: 100 }, (_, i) => i + 1).map(level => {
              const isUnlocked = level <= userProgress.currentLevel;
              const isCompleted = userProgress.completedLevels?.includes(level);
              const tier = getTierForLevel(level);
              const isRewardLevel = LEVEL_REWARDS[level];

              return (
                <button
                  key={level}
                  onClick={() => isUnlocked && startLevel(level)}
                  disabled={!isUnlocked}
                  className={`aspect-square rounded-lg flex items-center justify-center font-bold text-sm transition-all ${
                    isCompleted
                      ? 'bg-emerald-500/30 text-emerald-400 border border-emerald-500/50'
                      : isUnlocked
                      ? `bg-${tier.color}-500/20 text-white border border-${tier.color}-500/30 hover:scale-110`
                      : 'bg-zinc-900 text-zinc-600 border border-zinc-800'
                  } ${isRewardLevel ? 'ring-2 ring-gold ring-offset-2 ring-offset-zinc-900' : ''}`}
                >
                  {isUnlocked ? level : <Lock size={14} />}
                </button>
              );
            })}
          </div>

          <div className="flex gap-4">
            <Button
              onClick={() => startLevel(userProgress.currentLevel)}
              className="flex-1 py-4 bg-gradient-to-r from-yellow-500 to-orange-600 text-black font-bold"
            >
              Start Level {userProgress.currentLevel}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Playing Screen
  if (gameState === 'playing') {
    const tierConfig = getTierForLevel(selectedLevel);
    const portfolioValue = cash + (shares * currentPrice);
    const priceChange = entryPrice ? ((currentPrice - entryPrice) / entryPrice * 100) : 0;

    return (
      <div className="min-h-screen noise-bg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-bold bg-${tierConfig.color}-500/20 text-${tierConfig.color}-400`}>
                  Level {selectedLevel}
                </span>
                <span className="text-zinc-500">{tierConfig.name}</span>
              </div>
              <h2 className="text-xl font-bold text-white">{stock?.name}</h2>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-zinc-500 text-sm">Target</p>
                <p className="text-gold font-bold">+{tierConfig.targetReturn}%</p>
              </div>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${
                timeLeft <= 10 ? 'bg-red-500/20 text-red-400 animate-pulse' : 'bg-zinc-800 text-white'
              }`}>
                <Clock size={20} />
                <span className="font-mono text-xl font-bold">{timeLeft}s</span>
              </div>
            </div>
          </div>

          {/* Target Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-zinc-400">Progress to Target</span>
              <span className={currentReturn >= tierConfig.targetReturn ? 'text-emerald-400' : 'text-white'}>
                {currentReturn.toFixed(2)}% / {tierConfig.targetReturn}%
              </span>
            </div>
            <div className="w-full h-3 bg-zinc-800 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all ${
                  currentReturn >= tierConfig.targetReturn 
                    ? 'bg-emerald-500' 
                    : currentReturn >= 0 
                    ? 'bg-yellow-500' 
                    : 'bg-red-500'
                }`}
                style={{ width: `${Math.min(100, Math.max(0, (currentReturn / tierConfig.targetReturn) * 100))}%` }}
              />
            </div>
            {levelPassed && (
              <p className="text-emerald-400 text-sm mt-1 font-bold">🎯 Target reached! Keep going or finish early!</p>
            )}
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Chart */}
            <div className="lg:col-span-2 bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-4xl font-bold text-white">${currentPrice.toFixed(2)}</p>
                  {entryPrice && (
                    <p className={`text-sm ${priceChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}% from entry
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-zinc-500 text-sm">Your Return</p>
                  <p className={`text-3xl font-bold ${
                    currentReturn >= tierConfig.targetReturn ? 'text-emerald-400' : currentReturn >= 0 ? 'text-white' : 'text-red-400'
                  }`}>
                    {currentReturn >= 0 ? '+' : ''}{currentReturn.toFixed(2)}%
                  </p>
                </div>
              </div>

              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={priceHistory}>
                    <XAxis dataKey="time" hide />
                    <YAxis domain={['auto', 'auto']} hide />
                    {entryPrice && <ReferenceLine y={entryPrice} stroke="#f59e0b" strokeDasharray="5 5" />}
                    <Line 
                      type="monotone" 
                      dataKey="price" 
                      stroke={currentPrice >= startPrice ? '#10b981' : '#ef4444'}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 mt-6">
                <Button
                  onClick={handleBuy}
                  disabled={cash <= 0}
                  className="flex-1 py-6 text-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                >
                  <TrendUp size={24} className="mr-2" />
                  BUY
                </Button>
                <Button
                  onClick={handleSell}
                  disabled={shares <= 0}
                  className="flex-1 py-6 text-lg bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  <TrendDown size={24} className="mr-2" />
                  SELL
                </Button>
                {levelPassed && (
                  <Button
                    onClick={() => {
                      clearInterval(gameLoopRef.current);
                      clearInterval(newsIntervalRef.current);
                      setGameState('results');
                    }}
                    className="py-6 px-8 text-lg bg-gold hover:bg-yellow-600 text-black"
                  >
                    ✓ Finish
                  </Button>
                )}
              </div>

              {/* Position Info */}
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
                  <p className="text-zinc-500 text-xs">Cash</p>
                  <p className="text-white font-bold">${cash.toFixed(0)}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
                  <p className="text-zinc-500 text-xs">Shares</p>
                  <p className="text-white font-bold">{shares}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
                  <p className="text-zinc-500 text-xs">Portfolio</p>
                  <p className="text-white font-bold">${portfolioValue.toFixed(0)}</p>
                </div>
              </div>
            </div>

            {/* News Feed */}
            <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
              <div className="flex items-center gap-2 mb-4">
                <Newspaper size={24} className="text-yellow-400" />
                <h3 className="text-white font-bold text-lg">Breaking News</h3>
              </div>

              <AnimatePresence>
                {news.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">Waiting for news...</p>
                ) : (
                  <div className="space-y-3">
                    {news.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, x: 20, scale: 0.9 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0 }}
                        className={`p-4 rounded-xl border ${
                          item.category === 'positive' 
                            ? 'bg-emerald-500/10 border-emerald-500/30' 
                            : item.category === 'negative'
                            ? 'bg-red-500/10 border-red-500/30'
                            : 'bg-zinc-800/50 border-zinc-700'
                        } ${index === 0 ? 'ring-2 ring-yellow-500/50' : ''}`}
                      >
                        <p className="text-white text-sm">{item.text}</p>
                        <p className="text-zinc-500 text-xs mt-1">{item.time}</p>
                      </motion.div>
                    ))}
                  </div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results Screen
  if (gameState === 'results') {
    const tierConfig = getTierForLevel(selectedLevel);
    const passed = currentReturn >= tierConfig.targetReturn;

    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className={`inline-flex p-6 rounded-full ${passed ? 'bg-emerald-500/20' : 'bg-red-500/20'} mb-6`}>
              {passed ? (
                <Trophy size={64} weight="fill" className="text-emerald-400" />
              ) : (
                <Target size={64} className="text-red-400" />
              )}
            </div>

            <h1 className="text-3xl font-unbounded font-bold text-white mb-2">
              Level {selectedLevel} {passed ? 'Complete!' : 'Failed'}
            </h1>
            <p className="text-zinc-400 mb-8">
              {passed 
                ? 'Great job! You hit your profit target!' 
                : `You needed +${tierConfig.targetReturn}% but only got ${currentReturn.toFixed(2)}%`}
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
                <p className="text-zinc-500 text-sm">Your Return</p>
                <p className={`text-3xl font-bold ${currentReturn >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {currentReturn >= 0 ? '+' : ''}{currentReturn.toFixed(2)}%
                </p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
                <p className="text-zinc-500 text-sm">Target</p>
                <p className="text-2xl font-bold text-gold">+{tierConfig.targetReturn}%</p>
              </div>
            </div>

            {/* Reward display */}
            {passed && !reward && (
              <Button
                onClick={handleLevelComplete}
                className="w-full py-4 bg-gradient-to-r from-yellow-500 to-orange-600 text-black font-bold mb-4"
              >
                <Coins size={24} className="mr-2" />
                Claim Reward & Continue
              </Button>
            )}

            {reward && reward.cash > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-500/30 rounded-xl p-6 mb-8"
              >
                <p className="text-yellow-400 font-bold text-lg mb-2">🎉 Milestone Reward!</p>
                <p className="text-4xl font-bold text-gold mb-2">+${reward.cash.toLocaleString()}</p>
                <p className="text-zinc-400 text-sm">Added to your main portfolio!</p>
                {reward.badge && (
                  <p className="text-white mt-2">Badge earned: {reward.badge}</p>
                )}
              </motion.div>
            )}

            <div className="flex gap-4">
              {!passed && (
                <Button
                  onClick={() => startLevel(selectedLevel)}
                  className="flex-1 py-4 bg-zinc-800 hover:bg-zinc-700"
                >
                  Retry Level {selectedLevel}
                </Button>
              )}
              {passed && (
                <Button
                  onClick={() => {
                    if (!reward) handleLevelComplete();
                    else if (selectedLevel < 100) startLevel(selectedLevel + 1);
                    else setGameState('menu');
                  }}
                  className="flex-1 py-4 bg-emerald-600 hover:bg-emerald-700"
                >
                  {selectedLevel < 100 ? `Next: Level ${selectedLevel + 1} →` : 'Back to Menu'}
                </Button>
              )}
              <Button
                onClick={() => setGameState('menu')}
                className="py-4 px-6 bg-zinc-800 hover:bg-zinc-700"
              >
                Menu
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return null;
};

export default NewsReactor;
